import api from "@shared/api/client";
import type { Address } from "@shared/types/models";
import { extractErrorMessage } from "@shared/utils";
import type { TFunction } from "i18next";
import { useCallback } from "react";
import type { AddressesState, SetAddressesState } from "./types";

const useAddressCrud = (dialogState: AddressesState["dialogState"], setState: SetAddressesState, fetchData: () => Promise<void>, t: TFunction) => {
  const openCreate = useCallback(() => {
    setState({ dialogState: { mode: "edit", editing: null, name: "", error: "" } });
  }, [setState]);

  const openEdit = useCallback(
    (a: Address) => {
      setState({ dialogState: { mode: "edit", editing: a, name: a.name, error: "" } });
    },
    [setState],
  );

  const handleSave = useCallback(async () => {
    if (!dialogState.name.trim()) return;
    setState({ dialogState: { ...dialogState, error: "" } });
    try {
      if (dialogState.editing) await api.addresses.update(dialogState.editing.id, dialogState.name);
      else await api.addresses.create(dialogState.name);
      setState({ dialogState: { mode: "closed", editing: null, name: "", error: "" } });
      await fetchData();
    } catch (err: unknown) {
      setState({ dialogState: { ...dialogState, error: extractErrorMessage(err, t("addresses.saveError")) } });
    }
  }, [dialogState, setState, fetchData, t]);

  const handleDelete = useCallback(
    async (id: number) => {
      if (!confirm(t("deleteConfirmPrompt"))) return;
      try {
        await api.addresses.remove(id);
        await fetchData();
      } catch (err: unknown) {
        alert(extractErrorMessage(err, t("addresses.deleteError")));
      }
    },
    [fetchData, t],
  );

  return { openCreate, openEdit, handleSave, handleDelete };
};

export { useAddressCrud };
